import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import GovDoctypeDeepContent from "@/components/GovDoctypeDeepContent";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { findGovDoc, GOV_DOCTYPES } from "@/data/government-doctypes";

export const Route = createFileRoute("/en/certify/government/$doctype")({
  loader: ({ params }) => {
    const d = findGovDoc(params.doctype);
    if (!d) throw notFound();
    return { d };
  },
  head: ({ params }) => {
    const d = findGovDoc(params.doctype);
    if (!d) return { meta: [{ title: ctaTitle("Not found", "/en/certify/government/$doctype", "en")}, { name: "robots", content: "noindex" }] };
    const path = `/en/certify/government/${d.slug}`;
    const TITLE = `Certify ${d.en} — Translate + MFA + Apostille | NYC Thailand`;
    const DESC = `Certified translation + MFA + Apostille (Hague, in force 15 Dec 2025) for ${d.en} (${d.th}), ${d.turnaround}. ${d.usage} — Message us for a same-day answer.`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/en/certify/government/$doctype", "en")}, { name: "description", content: ctaDesc(DESC, "/en/certify/government/$doctype", "en")},
        { property: "og:title", content: ctaTitle(TITLE, "/en/certify/government/$doctype", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/certify/government/$doctype", "en")},
        { property: "og:url", content: `https://nyclanguageinstitute.com${path}` },
      ],
      links: canonicalAndHreflang(path, `/certify/government/${d.slug}`),
      scripts: buildLandingSchemas({
        path, title: TITLE, description: DESC,
        serviceType: `Document Certification — ${d.en}`, priceFromThb: d.translatePrice + d.mfaFee,
        steps: [
          { name: "Source review", text: `Must be an original issued by ${d.issuer} with all required stamps.` },
          { name: "Certified translation", text: "Translated into destination language (English / Chinese / Japanese / EU sworn)." },
          { name: "MFA legalization", text: "Sealed by Consular Affairs, Ministry of Foreign Affairs (Chaeng Watthana)." },
          { name: "Apostille or Embassy", text: "Apostille for Hague members / Embassy Legalization for non-Hague." },
          { name: "Delivery", text: "Free EMS in Thailand · DHL/FedEx 2–5 business days worldwide." },
        ],
        faqs: [
          { q: `What is ${d.en} used for abroad?`, a: d.usage },
          { q: "Most common rejection cause?", a: d.pitfall },
          { q: "Pricing and timeline?", a: `Translation ฿${d.translatePrice.toLocaleString()} + MFA ฿${d.mfaFee.toLocaleString()} = from ฿${(d.translatePrice + d.mfaFee).toLocaleString()}, ${d.turnaround}.` },
          { q: "Issuing authority?", a: d.issuer },
          { q: "Apostille or Embassy?", a: "Apostille is accepted by 125+ Hague Convention members — including China (2023), the UAE (2024), and Thailand itself since 15 Dec 2025 — so one MFA seal replaces the destination embassy step. Non-Hague destinations (Canada, Egypt, and most Gulf states outside UAE) still require full embassy legalization: MFA → destination embassy in Bangkok, adding 3–7 business days and consular fees on top of the MFA stamp." },
        ],
        breadcrumbs: [
          { name: "Home", path: "/en" },
          { name: "Government Certification", path: "/en/certify/government" },
          { name: d.en, path },
        ],
      }),
    };
  },
  component: Page,
  notFoundComponent: () => (
    <main className="p-12"><h1 className="text-2xl font-bold">Document type not found</h1>
    <Link to="/en/certify/government/$doctype" params={{ doctype: "birth-certificate" }} className="text-primary underline mt-4 block">← See all document types</Link>
          <QuotableStatsStrip cluster="translate" locale="en" variant="light" />
    </main>
  ),
});

function Page() {
  const { d } = Route.useLoaderData();
  return (
    <>
    <ServiceLanding
      badge="Gov · MFA · Apostille"
      title={`Certify ${d.en}`}
      subtitle={`${d.th} — ${d.usage}`}
      quickAnswer={{
        q: `What certification does ${d.en} need for overseas use?`,
        a: `Certified translation + MFA (Consular Affairs) + Apostille (Hague) or Embassy Legalization — from ฿${(d.translatePrice + d.mfaFee).toLocaleString()}, ${d.turnaround}. ${d.usage}`,
      }}
      heroBullets={[
        `Issued by ${d.issuer}`,
        `From ฿${(d.translatePrice + d.mfaFee).toLocaleString()}`,
        `${d.turnaround}`,
        "Includes translation + MFA + EMS",
      ]}
      whoFor={{
        th: ["Visa / document applicants abroad", "Students needing academic certification", "International couples"],
        foreign: [`Foreigners requesting Thai ${d.en} for use abroad`],
      }}
      steps={[
        { name: "Source review", text: `Original from ${d.issuer}` },
        { name: "Certified translation", text: "By MFA/MoJ-registered translators" },
        { name: "MFA legalization", text: "Consular Affairs, Chaeng Watthana" },
        { name: "Apostille or Embassy", text: "Depends on destination" },
        { name: "Delivery", text: "EMS/DHL worldwide" },
      ]}
      pricing={[
        { label: `Translate ${d.en}`, price: d.translatePrice.toLocaleString(), note: "per document" },
        { label: "MFA Legalization", price: d.mfaFee.toLocaleString(), note: "fee + processing" },
        { label: "Apostille (Hague)", price: "1,500", note: "130 countries" },
        { label: "Embassy Legalization", price: "1,200+", note: "non-Hague" },
        { label: "Express 24h", price: "+50%", note: "subject to MFA queue" },
      ]}
      faqs={[
        { q: "What is it used for?", a: d.usage },
        { q: "Most common rejection cause?", a: d.pitfall },
        { q: "Issuing authority?", a: d.issuer },
        { q: "Pricing and timeline?", a: `฿${(d.translatePrice + d.mfaFee).toLocaleString()}+, ${d.turnaround}` },
        { q: "Apostille vs Embassy?", a: "Apostille covers 125+ Hague members (latest: China + UAE). Non-Hague destinations require Embassy Legalization at the destination embassy in Thailand." },
      ]}
      relatedLinks={[
        ...GOV_DOCTYPES.filter((x) => x.slug !== d.slug).slice(0, 6).map((x) => ({
          to: `/en/certify/government/${x.slug}` as const, label: `Certify ${x.en}`,
        })) as any,
        { to: "/en/apostille", label: "Apostille service" },
        { to: "/en/translate", label: "Certified Translation" },
        { to: "/en/embassy", label: "Embassy Legalization" },
      ]}
    />
    <GovDoctypeDeepContent d={d} lang="en" />
    </>
  );
}
